import { useContext, useEffect, useState } from "react";
import { getCollectionRequest } from "services/apiRequests";
import { SchoolListType } from "utils/interfaces";
import { MainContext } from "components/utils/Contexts";
import SchoolsDropdownButton from "./SchoolsDropdownButton";
import { usePlatform } from "components/utils/PlatformContext";
import { logoutFunction } from "services/authRequests";

interface ViewTabBarProps {
  activeItem: string;
  tabOptions: string[];
  showSchoolsDropdown?: boolean;
  setActiveItem: React.Dispatch<React.SetStateAction<string>>;
  headerRef: React.MutableRefObject<any>;
  filtersPanel?: JSX.Element
}

const ViewTabBar: React.FC<ViewTabBarProps> = ({
  activeItem,
  tabOptions,
  showSchoolsDropdown = false,
  setActiveItem,
  headerRef,
  filtersPanel,
}) => {
  // States definition
  const [showSchoolListTab, setShowSchoolListTab] = useState(false);
  // State used for classes dropdown list in the "master admin" view on classes tab
  const [schoolData, setSchoolData] = useState<SchoolListType[]>([]);

  // Get the context parameters
  const { classesTerminology, studentsTerminology } = usePlatform();
  const { contextSchool, setContextSchool } = useContext(MainContext);

  // Function used when option is clicked
  const handleOnClick = (option: string) => {
    return () => {
      // Set the active lesson
      setActiveItem(option.toLowerCase());
      // And the header names
      headerRef.current.setHeaderTitle(option === "Classes" ? classesTerminology : option === "Students" ? studentsTerminology : option);
      headerRef.current.setHeaderDescription(
        `List of current ${option === "Classes" ? classesTerminology.toLowerCase() : option === "Students" ? studentsTerminology.toLowerCase() : option.toLowerCase()}`
      );
    };
  };

  /**
   * Function to get the school data
   * @returns data
   */
  const getSchoolData = async () => {
    // Get the school data
    const response = await getCollectionRequest("/api/organisations");

    // Check if the response is successful
    if (!response.successful) {
      // Check if it's not successful because of a token expiration
      if (response.logout) {
        // alert(response.message);
        return await logoutFunction();
      }

      return [];
    }

    // Get the data
    const data = response.content;

    // Assign the data to the corresponding element
    setSchoolData(data);

    // And assign selected data as the first school
    if (setContextSchool && !contextSchool) setContextSchool(data[0]);

    return data;
  };

  /**
   * Root definition
   */
  useEffect(() => {
    if (showSchoolsDropdown) getSchoolData();
  }, []);

  return (
    <div className="inline-flex gap-4">
      <div className="p-1.5 bg-Tab rounded-full justify-start items-center gap-1.5 flex">
        {showSchoolsDropdown && (
          <>
            <SchoolsDropdownButton
              showSchoolListTab={showSchoolListTab}
              schoolData={schoolData}
              contextSchool={contextSchool}
              setContextSchool={setContextSchool}
              setShowSchoolListTab={setShowSchoolListTab}
            />
            <div className="self-stretch py-1 flex-col opacity-50 justify-center items-start gap-2 inline-flex">
              <div className="grow shrink basis-0 w-px self-stretch bg-neutral-300" />
            </div>
          </>
        )}
        {tabOptions.map((option) => (
          <button
            key={option}
            className={
              activeItem === option.toLowerCase()
                ? "px-3 py-1.5 bg-white rounded-full justify-center items-center gap-2 flex cursor-pointer"
                : "px-3 py-1.5 rounded-full justify-center items-center gap-2 flex cursor-pointer hover:bg-neutral-200 ease-in-out duration-300"
            }
            onClick={handleOnClick(option)}
          >
            <div className="text-secondary-600 text-sm font-semibold font-poppins leading-[17px] select-none">
              {option === "Classes" ? classesTerminology : option === "Students" ? studentsTerminology : option}
            </div>
          </button>
        ))}
      </div>
      {filtersPanel}
    </div>
  );
};

export default ViewTabBar;
